const GOOGLE_H5_SRC = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
const DEFAULT_TIMEOUT_MS = 15000;
const SCRIPT_TIMEOUT_MS = 7000;

const SCRIPT_PROMISES = new Map();

function hasDOM() {
  return typeof window !== 'undefined' && typeof document !== 'undefined' && !!document.createElement;
}

function normalizeReason(status) {
  if (!status) return 'no-fill';
  if (status === 'error') return 'error';
  if (status === 'frequencyCapped' || status === 'ignored' || status === 'blocked') return 'blocked';
  return 'no-fill';
}

function appendClientParam(src, client) {
  const join = src.includes('?') ? '&' : '?';
  return `${src}${join}client=${encodeURIComponent(client)}`;
}

function loadGoogleH5Script(client, log) {
  if (!hasDOM()) return Promise.resolve({ ok: false, reason: 'blocked' });
  if (!client) return Promise.resolve({ ok: false, reason: 'not-configured' });

  const src = appendClientParam(GOOGLE_H5_SRC, client);
  if (SCRIPT_PROMISES.has(src)) return SCRIPT_PROMISES.get(src);

  const existing = document.querySelector?.(`script[src="${src}"]`);
  if (existing) {
    const p = Promise.resolve({ ok: true });
    SCRIPT_PROMISES.set(src, p);
    return p;
  }

  const promise = new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };
    const timer = setTimeout(() => {
      log.warn('[ads:googleH5] script load timed out');
      finish({ ok: false, reason: 'blocked' });
    }, SCRIPT_TIMEOUT_MS);

    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.crossOrigin = 'anonymous';
    script.setAttribute('data-ad-client', client);
    script.onload = () => {
      log.debug('[ads:googleH5] script loaded');
      finish({ ok: true });
    };
    script.onerror = () => {
      log.warn('[ads:googleH5] script load failed');
      finish({ ok: false, reason: 'blocked' });
    };
    (document.head || document.body || document.documentElement).appendChild(script);
  });

  SCRIPT_PROMISES.set(src, promise);
  return promise;
}

export class GoogleH5Provider {
  constructor(cfg, ctx = {}) {
    this.cfg = cfg || {};
    this.name = 'googleH5';
    this.log = ctx.log || console;
    this.emit = ctx.emit || (() => {});
    this.timeoutMs = Number(this.cfg.timeoutMs) > 0 ? Number(this.cfg.timeoutMs) : DEFAULT_TIMEOUT_MS;
    this._ready = null;
  }

  async init() {
    if (!this.cfg.client) {
      this.log.warn('[ads:googleH5] client is not configured');
      return { ok: false, reason: 'not-configured' };
    }

    this._ready = loadGoogleH5Script(this.cfg.client, this.log);
    const ready = await this._ready;
    if (!ready.ok) return ready;

    try {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adConfig?.({
        preloadAdBreaks: 'on',
        sound: this.cfg.sound === 'off' ? 'off' : 'on',
      });
    } catch (err) {
      this.log.warn('[ads:googleH5] adConfig failed', err);
      return { ok: false, reason: 'error' };
    }

    return { ok: true };
  }

  async interstitial(placement = 'default') {
    if (!this.cfg.interstitialSlot) {
      return { shown: false, reason: 'not-configured' };
    }
    return this._requestAd({
      kind: 'interstitial',
      type: 'next',
      name: this.cfg.interstitialSlot || placement,
      placement,
    });
  }

  async rewarded(placement = 'default') {
    if (!this.cfg.rewardedSlot) {
      return { shown: false, granted: false, reason: 'not-configured' };
    }

    const result = await this._requestAd({
      kind: 'rewarded',
      type: 'reward',
      name: this.cfg.rewardedSlot || placement,
      placement,
      rewarded: true,
    });

    return {
      shown: !!result.shown,
      granted: !!result.granted,
      ...(result.reason ? { reason: result.reason } : {}),
    };
  }

  async _requestAd({ kind, type, name, placement, rewarded = false }) {
    const ready = await (this._ready || this.init());
    if (!ready?.ok) {
      const reason = ready?.reason || 'blocked';
      this.log.debug(`[ads:googleH5] ${kind} unavailable: ${reason}`, placement);
      return rewarded ? { shown: false, granted: false, reason } : { shown: false, reason };
    }

    if (typeof window.adBreak !== 'function') {
      this.log.warn(`[ads:googleH5] ${kind} blocked: adBreak unavailable`);
      return rewarded ? { shown: false, granted: false, reason: 'blocked' } : { shown: false, reason: 'blocked' };
    }

    this.log.debug(`[ads:googleH5] ${kind} ad request started`, { placement, name });

    return new Promise((resolve) => {
      let settled = false;
      let shown = false;
      let granted = false;
      let opened = false;

      // Announce that an ad is actually on screen (used to pause/mute the game).
      const open = () => { if (!opened) { opened = true; this.emit('ad:open', { placement, kind }); } };

      const finish = (result) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (result.shown) this.log.debug(`[ads:googleH5] ${kind} ad shown`, { placement, name });
        else this.log.debug(`[ads:googleH5] ${kind} ${result.reason || 'no-fill'}`, { placement, name });
        // Always balance an open with a close, even on timeout.
        if (opened) this.emit('ad:close', { placement, kind, granted: !!result.granted });
        resolve(result);
      };

      const timer = setTimeout(() => {
        finish(rewarded
          ? { shown: false, granted: false, reason: 'no-fill' }
          : { shown: false, reason: 'no-fill' });
      }, this.timeoutMs);

      try {
        window.adBreak({
          type,
          name,
          beforeAd: () => { shown = true; open(); },
          afterAd: () => {
            if (!rewarded) finish({ shown: true });
          },
          beforeReward: rewarded ? (showAdFn) => {
            shown = true;
            open();
            if (typeof showAdFn === 'function') showAdFn();
          } : undefined,
          adViewed: rewarded ? () => { granted = true; } : undefined,
          adDismissed: rewarded ? () => { finish({ shown: true, granted }); } : undefined,
          adBreakDone: (info = {}) => {
            if (rewarded) {
              if (shown || granted) finish({ shown: true, granted });
              else finish({ shown: false, granted: false, reason: normalizeReason(info.breakStatus) });
              return;
            }
            if (shown) finish({ shown: true });
            else finish({ shown: false, reason: normalizeReason(info.breakStatus) });
          },
        });
      } catch (err) {
        this.log.warn(`[ads:googleH5] ${kind} error`, err);
        finish(rewarded
          ? { shown: false, granted: false, reason: 'error' }
          : { shown: false, reason: 'error' });
      }
    });
  }
}
